import React from 'react';
import { 
  Bell, 
  Settings, 
  Check, 
  Trash2,
  AlertTriangle,
  Package,
  CreditCard,
  Users,
  Calendar,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

const notifications = [
  {
    id: 1,
    type: 'alert',
    icon: AlertTriangle,
    title: 'Low Stock Alert',
    message: 'Chicken tikka masala has fallen below par level (2.5 kg remaining)',
    time: '2 min ago',
    read: false,
    category: 'inventory',
  },
  {
    id: 2,
    type: 'approval',
    icon: CreditCard,
    title: 'Approval Required',
    message: 'Expense claim kr 55 000 for Coffee Machine is pending your approval',
    time: '15 min ago',
    read: false,
    category: 'approvals',
  },
  {
    id: 3,
    type: 'info',
    icon: Users,
    title: 'Shift Reminder',
    message: 'Evening shift starts in 30 minutes. 4 staff members scheduled.',
    time: '30 min ago',
    read: false,
    category: 'hr',
  },
  {
    id: 4,
    type: 'success',
    icon: Package,
    title: 'Order Delivered',
    message: 'Purchase Order #PO-2024-089 has been delivered and received',
    time: '1 hour ago',
    read: true,
    category: 'inventory',
  },
  {
    id: 5,
    type: 'info',
    icon: Calendar,
    title: 'Reservation Confirmed',
    message: 'New reservation for 6 guests at 7:30 PM confirmed',
    time: '2 hours ago',
    read: true,
    category: 'reservations',
  },
  {
    id: 6,
    type: 'alert',
    icon: Clock,
    title: 'License Expiry Warning',
    message: 'Food hygiene certificate expires in 30 days. Renewal required.',
    time: '3 hours ago',
    read: true,
    category: 'compliance',
  },
];

const typeConfig = {
  alert: { color: 'text-spice-tandoori', bg: 'bg-spice-tandoori/10' },
  approval: { color: 'text-spice-turmeric', bg: 'bg-spice-turmeric/10' },
  info: { color: 'text-blue-500', bg: 'bg-blue-50' },
  success: { color: 'text-spice-cardamom', bg: 'bg-spice-cardamom/10' },
};

export function Notifications() {
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div data-testid="notifications-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-brand-charcoal">Notifications</h1>
          <p className="text-gray-500 text-sm mt-1">
            {unreadCount > 0 ? `You have ${unreadCount} unread notifications` : 'All caught up!'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2">
            <Check className="w-4 h-4" />
            Mark All Read
          </Button>
          <Button variant="outline" size="icon">
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Notification Categories */}
      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">
            All
            <Badge variant="secondary" className="ml-2">{notifications.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="unread">
            Unread
            <Badge className="ml-2 bg-brand-orange">{unreadCount}</Badge>
          </TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="approvals">Approvals</TabsTrigger>
          <TabsTrigger value="hr">HR</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {notifications.map((notification) => {
                  const config = typeConfig[notification.type];
                  const Icon = notification.icon;
                  
                  return (
                    <div
                      key={notification.id}
                      data-testid={`notification-${notification.id}`}
                      className={cn(
                        'flex items-start gap-4 p-4 transition-colors hover:bg-gray-50',
                        !notification.read && 'bg-brand-orange/5'
                      )}
                    >
                      <Checkbox className="mt-1" />
                      <div className={cn('p-2 rounded-lg flex-shrink-0', config.bg)}>
                        <Icon className={cn('w-5 h-5', config.color)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={cn(
                            'font-medium text-brand-charcoal',
                            !notification.read && 'font-semibold'
                          )}>
                            {notification.title}
                          </p>
                          {!notification.read && (
                            <span className="w-2 h-2 bg-brand-orange rounded-full" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-0.5">{notification.message}</p>
                        <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="unread" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {notifications.filter(n => !n.read).map((notification) => {
                  const config = typeConfig[notification.type];
                  const Icon = notification.icon;
                  
                  return (
                    <div
                      key={notification.id}
                      className="flex items-start gap-4 p-4 bg-brand-orange/5"
                    >
                      <Checkbox className="mt-1" />
                      <div className={cn('p-2 rounded-lg flex-shrink-0', config.bg)}>
                        <Icon className={cn('w-5 h-5', config.color)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-brand-charcoal">{notification.title}</p>
                          <span className="w-2 h-2 bg-brand-orange rounded-full" />
                        </div>
                        <p className="text-sm text-gray-600 mt-0.5">{notification.message}</p>
                        <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Placeholder for other tabs */}
        <TabsContent value="inventory" className="mt-4">
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Inventory notifications will appear here</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approvals" className="mt-4">
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Approval notifications will appear here</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hr" className="mt-4">
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>HR notifications will appear here</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Notification Settings Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Notification Preferences</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-4 h-4 text-brand-orange" />
                <span className="font-medium">Push Notifications</span>
              </div>
              <p className="text-sm text-gray-500">Enabled for critical alerts</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-4 h-4 text-brand-orange" />
                <span className="font-medium">Email Notifications</span>
              </div>
              <p className="text-sm text-gray-500">Daily digest at 9:00 AM</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-4 h-4 text-brand-orange" />
                <span className="font-medium">SMS Alerts</span>
              </div>
              <p className="text-sm text-gray-500">Critical alerts only</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
